import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

export type ChartConfig = {
  [key: string]: {
    label?: React.ReactNode;
    color?: string;
    shortLabel?: string;
  };
};

type TooltipValue = number | string | Array<number | string>;

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a ChartContainer.');
  }
  return context;
}

function buildChartVars(config: ChartConfig): React.CSSProperties {
  const style: Record<string, string> = {};
  Object.entries(config).forEach(([key, item]) => {
    if (item.color) {
      style[`--color-${key}`] = item.color;
    }
  });
  return style as React.CSSProperties;
}

type ChartContainerProps = React.ComponentProps<'div'> & {
  config: ChartConfig;
  children: React.ReactNode;
  family?: 'default' | 'multi' | 'single' | 'comparison' | 'allocation';
};

export function ChartContainer({ id, className, children, config, family = 'default', ...props }: ChartContainerProps) {
  const uniqueId = React.useId().replace(/:/g, '');
  const chartId = `chart-${id ?? uniqueId}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        data-family={family}
        className={cn('h-72 min-h-[18rem] w-full min-w-0', className)}
        style={buildChartVars(config)}
        {...props}
      >
        <div
          className={cn(
            'brand-chart-surface relative h-full min-h-[18rem] min-w-0 overflow-hidden rounded-[1.25rem] border border-border/70 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_20px_36px_-30px_rgba(8,35,53,0.45)] sm:p-4',
            'before:pointer-events-none before:absolute before:inset-x-4 before:top-0 before:h-px before:bg-white/60 before:content-[""]',
            '[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-axis-tick_text]:text-[11px] [&_.recharts-cartesian-grid_line]:stroke-border/50',
            '[&_.recharts-dot[stroke="#fff"]]:stroke-card [&_.recharts-label-list_text]:fill-foreground [&_.recharts-label-list_text]:font-medium',
            '[&_.recharts-pie-sector]:stroke-card [&_.recharts-pie-sector]:stroke-2 [&_.recharts-reference-line_line]:stroke-border/60'
          )}
        >
          <RechartsPrimitive.ResponsiveContainer minWidth={0} minHeight={0}>
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        </div>
      </div>
    </ChartContext.Provider>
  );
}

export const ChartTooltip = RechartsPrimitive.Tooltip;

type ChartLegendPillsProps = {
  config?: ChartConfig;
  keys?: readonly string[];
  activeKeys?: readonly string[];
  onActiveKeysChange?: (keys: string[]) => void;
  className?: string;
};

export function ChartLegendPills({ config: configProp, keys, activeKeys, onActiveKeysChange, className }: ChartLegendPillsProps) {
  const context = React.useContext(ChartContext);
  const config = configProp ?? context?.config;

  if (!config) {
    throw new Error('ChartLegendPills requires either a config prop or a surrounding ChartContainer.');
  }

  const resolvedKeys = keys ?? Object.keys(config);
  const resolvedActiveKeys = activeKeys ?? resolvedKeys;

  if (resolvedKeys.length === 0) {
    return null;
  }

  if (onActiveKeysChange) {
    return (
      <ToggleGroup
        type="multiple"
        variant="outline"
        size="sm"
        value={[...resolvedActiveKeys]}
        onValueChange={(value) => onActiveKeysChange(value.length === 0 ? [...resolvedKeys] : value)}
        className={cn('flex flex-wrap items-center gap-2', className)}
      >
        {resolvedKeys.map((key) => {
          const itemConfig = config[key];
          return (
            <ToggleGroupItem key={key} value={key} className="rounded-full px-3">
              <span className="size-2 rounded-full" style={{ backgroundColor: itemConfig?.color ?? `var(--color-${key})` }} />
              <span>{itemConfig?.shortLabel ?? itemConfig?.label ?? key}</span>
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {resolvedKeys.map((key) => {
        const itemConfig = config[key];
        const isActive = resolvedActiveKeys.includes(key);

        return (
          <Badge
            key={key}
            variant="outline"
            className={cn('gap-2 border-border/80 bg-background/70 px-3 py-1 normal-case tracking-[0.02em]', !isActive && 'opacity-55')}
          >
            <span className="size-2 rounded-full" style={{ backgroundColor: itemConfig?.color ?? `var(--color-${key})` }} />
            <span>{itemConfig?.shortLabel ?? itemConfig?.label ?? key}</span>
          </Badge>
        );
      })}
    </div>
  );
}

type ChartTooltipContentProps = React.ComponentProps<'div'> & {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string | number;
    value?: TooltipValue;
    color?: string;
  }>;
  label?: string | number;
  hideLabel?: boolean;
  indicator?: 'line' | 'dot';
  labelFormatter?: (value: string) => React.ReactNode;
  formatter?: (value: TooltipValue, name: string) => React.ReactNode;
};

export function ChartTooltipContent({
  active,
  payload,
  label,
  className,
  hideLabel = false,
  indicator = 'dot',
  labelFormatter,
  formatter
}: ChartTooltipContentProps) {
  const { config } = useChart();

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'min-w-[13rem] rounded-2xl border border-border/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(247,242,234,0.94))] px-3.5 py-3 text-xs shadow-[0_18px_36px_-24px_rgba(8,35,53,0.62)] backdrop-blur',
        className
      )}
    >
      {!hideLabel ? (
        <div className="mb-2 border-b border-border/60 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {labelFormatter ? labelFormatter(String(label ?? '')) : label}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        {payload.map((item) => {
          const key = String(item.name ?? item.dataKey ?? '');
          const itemConfig = config[key];
          const color = item.color ?? `var(--color-${key})`;
          const itemLabel = itemConfig?.label ?? item.name ?? key;

          return (
            <div key={`${key}-${item.value}`} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                {indicator === 'line' ? (
                  <span className="h-0.5 w-3 rounded-full" style={{ backgroundColor: color }} />
                ) : (
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                )}
                <span>{itemLabel}</span>
              </div>
              <span className="font-semibold tabular-nums text-foreground">
                {formatter ? formatter(item.value ?? 0, key) : Number(item.value ?? 0).toLocaleString('en-US')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
